import React, { useState, useEffect, useRef } from 'react';
import styles from './OrganisationResearchPopup.module.css';
import { useAlphaSenseToken } from '../../context/AlphaSenseTokenContext';
import { useAuth } from '../../hooks/useAuth';
import { runWorkflowAgent, deepResearch } from '../../services/alphasenseService';

const OrganisationResearchPopup = ({ isOpen, onClose, agent: agentProp }) => {
  const { oboToken } = useAlphaSenseToken();
  const { getApiToken } = useAuth();

  const [step, setStep] = useState(1);
  const [customPrompt, setCustomPrompt] = useState('');

  const [running, setRunning] = useState(false);
  const [error, setError] = useState(null);
  const [retryCountdown, setRetryCountdown] = useState(0);
  const countdownRef = useRef(null);
  const runningRef = useRef(false);
  const retryCountdownRef = useRef(0);
  const textareaRef = useRef(null);

  useEffect(() => { runningRef.current = running; }, [running]);
  useEffect(() => { retryCountdownRef.current = retryCountdown; }, [retryCountdown]);
  useEffect(() => () => clearInterval(countdownRef.current), []);

  const startCountdown = (seconds) => {
    setRetryCountdown(seconds);
    clearInterval(countdownRef.current);
    countdownRef.current = setInterval(() => {
      setRetryCountdown((prev) => {
        if (prev <= 1) { clearInterval(countdownRef.current); setError(null); return 0; }
        return prev - 1;
      });
    }, 1000);
  };

  const resetForm = () => {
    setStep(1);
    setCustomPrompt('');
    setError(null);
  };

  // Reset stale state (e.g. an error from a request that finished in the background
  // while the popup was closed) whenever the popup opens or closes, unless a request
  // is still running or a retry countdown is actively waiting.
  useEffect(() => {
    if (!runningRef.current && retryCountdownRef.current === 0) resetForm();
  }, [isOpen]);

  useEffect(() => {
    if (step === 2 && !running) setTimeout(() => textareaRef.current?.focus(), 50);
  }, [step]);

  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [isOpen]);

  const canGenerate = customPrompt.trim().length > 0;

  const handleGenerateReport = async () => {
    if (running || !canGenerate) return;
    const agentId = agentProp?.id ?? agentProp?.agentId;
    if (!agentId) return;
    if (!oboToken) {
      setError('Session not ready — please wait a moment and try again.');
      return;
    }

    setRunning(true);
    setError(null);
    try {
      const msalToken = await getApiToken();
      const { conversationId } = await runWorkflowAgent(agentId, msalToken, oboToken);
      await deepResearch(customPrompt.trim(), conversationId, msalToken, oboToken);
      resetForm();
      onClose();
    } catch (err) {
      if (err.code === 'CREDIT_LIMIT_EXHAUSTED') {
        setError(err.message);
        if (err.retryAfterSeconds) startCountdown(err.retryAfterSeconds);
      } else if (err?.status === 403) {
        setError('You are not authorized to access AlphaSense');
      } else if (err?.status === 401) {
        setError('Your AlphaSense connection has expired or is invalid. Please refresh the page and try again.');
      } else {
        setError('Failed to generate report. Please try again.');
      }
    } finally {
      setRunning(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey) && canGenerate && !running) {
      handleGenerateReport();
    }
  };

  if (!isOpen) return null;

  const agentName = agentProp?.name ?? 'Workforce, Operating Model & Org Design';

  return (
    <div
      className={styles.overlay}
      role="dialog"
      aria-modal="true"
      aria-labelledby="org-research-popup-title"
    >
      <div className={`${styles.modal} ${step === 2 ? styles.modalLarge : ''}`}>
        <div className={styles.header}>
          {step === 2 ? (
            <button
              type="button"
              className={styles.backBtn}
              onClick={() => setStep(1)}
              disabled={running}
            >
              <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back
            </button>
          ) : (
            <h2 className={styles.title} id="org-research-popup-title">{agentName}</h2>
          )}
          <button
            type="button"
            className={styles.closeBtn}
            onClick={onClose}
            aria-label="Close"
          >
            <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {step === 1 && (
          <>
            <div className={styles.introBody}>
              <p className={styles.introText}>
                This is a very detailed framework for a workforce and operating model analysis. I can certainly conduct this research and synthesize the findings into the specific sections and format you&apos;ve outlined.
              </p>
              <p className={styles.introText}>
                To get started, I need a few key details to fill in the placeholders:
              </p>
              <ul className={styles.introList}>
                <li>Which <strong>company</strong> are we analyzing for the workforce transformation and benchmarking?</li>
                <li>Which <strong>five peer companies</strong> should be included in the operating model and org design benchmark?</li>
                <li>What <strong>industry</strong> should I use as the reference point for GBS maturity and general benchmarks?</li>
              </ul>
              <p className={styles.introText}>
                Once you provide these names, I will analyze the filings, transcripts, and research reports to cover every dimension requested—from the workforce cost baseline and AI impact to the detailed delivery tier and location strategy benchmarks.
              </p>
            </div>
            <button type="button" className={styles.nextBtn} onClick={() => setStep(2)}>
              Next
            </button>
          </>
        )}

        {step === 2 && (
          <>
            <div className={styles.chatPreview}>
              <div className={styles.agentMessage}>
                Run the Accenture Consulting <strong>{agentName}</strong> agent
              </div>
            </div>

            <div className={`${styles.promptBox} ${running ? styles.promptBoxDisabled : ''}`}>
              <textarea
                ref={textareaRef}
                className={styles.promptTextarea}
                value={customPrompt}
                onChange={(e) => setCustomPrompt(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Company Name, Peer companies, Industry"
                disabled={running}
                rows={3}
              />
              <div className={styles.promptToolbar}>
                <div className={styles.toolbarLeft}>
             
                
                </div>
                <button
                  type="button"
                  className={`${styles.generateBtn} ${styles.generateBtnInline}`}
                  onClick={handleGenerateReport}
                  disabled={running || !canGenerate || retryCountdown > 0}
                >
                  {running ? (
                    <>
                      <span className={styles.btnSpinner} />
                      Generating…
                    </>
                  ) : (
                    'Generate Report'
                  )}
                </button>
              </div>
            </div>

            {error && (
              <p className={styles.error}>
                {retryCountdown > 0 ? `Credit limit exceeded — retry in ${retryCountdown}s…` : error}
              </p>
            )}

            {running && (
              <>
                <p className={styles.statusInfo}>
                  You can continue using the application while we generate your report. It will be ready in approximately 10 minutes and sent to you via email.
                </p>
                <div className={styles.okayRow}>
                  <button type="button" className={styles.okayBtn} onClick={onClose}>Okay</button>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default OrganisationResearchPopup;
